"use client";

import React, { useState, useEffect } from "react";
import { Brain } from "lucide-react";
import { ProposalCard } from "@/components/board/ProposalCard";
import {
  getBoardProposalsAction,
  generateBoardProposalsAction,
} from "@/actions/shadow-board-actions";
import {
  getDashboardOverviewStatsAction,
  getSemanticLedgerLogsAction,
} from "@/actions/analytics-actions";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/feedback";
import {
  TrendingUp,
  Target,
  Users,
  FolderGit,
  Landmark,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

export default function DashboardPage() {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(true);

  const [stats, setStats] = useState<any>({
    goalsCount: 8,
    teamCount: 14,
    projectsCount: 6,
    revenue: "€142k",
    criticalGoalsCount: 1,
    watchProjectsCount: 2,
  });
  const [ledgerLogs, setLedgerLogs] = useState<any[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await getBoardProposalsAction();
        setProposals(data);
      } catch (err) {
        console.error("Failed to load board proposals:", err);
      } finally {
        setLoadingProposals(false);
      }
    }
    async function loadStatsAndLogs() {
      try {
        const statsRes = await getDashboardOverviewStatsAction();
        if (statsRes?.stats) {
          setStats(statsRes.stats);
        }

        const logsRes = await getSemanticLedgerLogsAction();
        if (logsRes?.success && logsRes.logs) {
          setLedgerLogs(logsRes.logs.slice(0, 3)); // display latest 3 events
        }
      } catch (err) {
        console.error("Failed to load dashboard stats/logs:", err);
      } finally {
        setLoadingStats(false);
      }
    }
    load();
    loadStatsAndLogs();
  }, []);

  const handleStatusChange = (
    id: string,
    newStatus: "pending" | "applied" | "rejected",
  ) => {
    setProposals((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p)),
    );
  };

  const handleGenerateProposal = async (risk: "low" | "medium" | "high") => {
    try {
      const res = await generateBoardProposalsAction(risk);
      if (res?.success && res.proposal) {
        setProposals((prev) => [res.proposal, ...prev]);
      }
    } catch (err) {
      console.error("Failed to generate board proposal:", err);
    }
  };

  const statsCards = [
    {
      name: "Obiettivi Strategici",
      value: stats.goalsCount.toString(),
      change: `${stats.criticalGoalsCount} a rischio/critici`,
      changeType: stats.criticalGoalsCount > 0 ? "error" : "success",
      icon: Target,
      category: "strategy",
    },
    {
      name: "Team & Collaboratori",
      value: stats.teamCount.toString(),
      change: "Tutti attivi nel workspace",
      changeType: "success",
      icon: Users,
      category: "people",
    },
    {
      name: "Progetti Attivi",
      value: stats.projectsCount.toString(),
      change: `${stats.watchProjectsCount} in watch state`,
      changeType: stats.watchProjectsCount > 0 ? "warning" : "success",
      icon: FolderGit,
      category: "work",
    },
    {
      name: "Fatturato Previsto",
      value: stats.revenue,
      change: "+12.4% vs trim. prec.",
      changeType: "success",
      icon: Landmark,
      category: "finance",
    },
  ];

  const formatTimeAgo = (dateStr: string) => {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Ora";
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    return `${diffDays} giorni fa`;
  };

  const displayLogs =
    ledgerLogs.length > 0
      ? ledgerLogs
      : [
          {
            id: "1",
            nodeType: "work",
            eventType: "UPDATE",
            description:
              "Creata la milestone 'Inizializzazione Design System' per il progetto Quario Data Platform.",
            createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          },
          {
            id: "2",
            nodeType: "risk",
            eventType: "UPDATE",
            description:
              "La milestone 'Stripe Sandbox Integration' presenta 2 decisioni aperte non verificate.",
            createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          },
          {
            id: "3",
            nodeType: "sales",
            eventType: "CREATE",
            description:
              "Il lead 'Delphinet Tech' è stato catturato ed associato alla pipeline strategica.",
            createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          },
        ];

  return (
    <div className="h-full overflow-y-auto px-8 py-8 scrollbar-thin">
      <div className="space-y-8 max-w-6xl mx-auto">
        {/* Hero Welcome banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-text-on-primary shadow-md">
          <div className="relative z-10 max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Sistema Operativo Aziendale</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              Benvenuto su Cleos
            </h1>
            <p className="text-white/80 text-sm leading-relaxed">
              Il Company Context Graph collega obiettivi, persone, lavoro,
              clienti e denaro in un unico grafo semantico relazionale.
              Visualizza le metriche e scopri le dipendenze nascoste.
            </p>
            <div className="pt-4 flex flex-wrap gap-3">
              <Link href="/explore">
                <Button className="bg-white text-primary-700 hover:bg-white/95 border border-transparent shadow-sm">
                  Esplora il Grafo
                </Button>
              </Link>
              <Link href="/strategy/manifesto">
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/10 border border-white/20"
                >
                  Leggi il Manifesto
                </Button>
              </Link>
            </div>
          </div>
          {/* Subtle background decoration */}
          <div className="absolute right-[-4rem] bottom-[-4rem] h-64 w-64 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute right-12 top-12 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        </div>

        {/* Grid of Key metrics */}
        <section className="space-y-4">
          <h2 className="text-lg font-bold tracking-tight text-text-primary">
            Stato della Compagnia
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {statsCards.map((stat) => {
              const Icon = stat.icon;
              const routes: Record<string, string> = {
                strategy: "/strategy/goals",
                people: "/people",
                work: "/work/projects",
                finance: "/finance/overview",
              };

              return (
                <Link
                  key={stat.name}
                  href={routes[stat.category] || "/"}
                  className="block group outline-none transition-all hover:scale-[1.01]"
                >
                  <Card className="flex flex-col justify-between h-full group-hover:border-primary-500/50 group-hover:shadow-sm transition-all">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider group-hover:text-primary-600 transition-colors">
                        {stat.name}
                      </span>
                      <div className="h-8 w-8 rounded-lg bg-card-muted flex items-center justify-center border border-border/50 text-text-secondary group-hover:bg-primary-600/10 group-hover:text-primary-600 group-hover:border-primary-600/20 transition-all">
                        <Icon className="h-4 w-4" />
                      </div>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="text-2xl font-bold text-text-primary">
                        {stat.value}
                      </div>
                    </CardContent>
                    <CardFooter className="flex items-center justify-between border-t border-border/20 pt-3 mt-2 text-xs">
                      <Badge variant={stat.category as any}>
                        {stat.category}
                      </Badge>
                      <span
                        className={
                          stat.changeType === "error"
                            ? "text-error font-medium"
                            : stat.changeType === "warning"
                              ? "text-warning font-medium"
                              : "text-success font-medium"
                        }
                      >
                        {stat.change}
                      </span>
                    </CardFooter>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Context updates & Quick creation */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Ultimi Eventi nel Grafo Semantico</CardTitle>
              <CardDescription>
                Flusso delle modifiche e delle relazioni create nel tenant.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flow-root">
                <ul role="list" className="-mb-8">
                  {displayLogs.map((event, eventIdx) => (
                    <li key={event.id}>
                      <div className="relative pb-8">
                        {eventIdx !== displayLogs.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-border"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span
                              className={cn(
                                "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-card border",
                                event.nodeType === "work" ||
                                  event.nodeType === "project" ||
                                  event.nodeType === "task" ||
                                  event.nodeType === "milestone"
                                  ? "bg-category-work-soft border-category-work-border text-category-work"
                                  : event.nodeType === "risk" ||
                                      event.nodeType === "decision"
                                    ? "bg-category-risk-soft border-category-risk-border text-category-risk"
                                    : "bg-category-sales-soft border-category-sales-border text-category-sales",
                              )}
                            >
                              <TrendingUp className="h-4 w-4" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-0 pt-1.5">
                            <p className="text-sm font-semibold text-text-primary">
                              {event.eventType === "completamento" ||
                              event.eventType === "Milestone Completed"
                                ? "Milestone completata"
                                : event.eventType === "rischio_blocco" ||
                                    event.eventType === "Risk Mitigated"
                                  ? "Impatto sul Grafo"
                                  : "Nuova Attività"}
                            </p>
                            {event.nodeId ? (
                              <Link
                                href={`/explore?nodeId=${event.nodeId}`}
                                className="text-xs text-text-secondary hover:text-primary-600 hover:underline transition-colors mt-0.5 block"
                              >
                                {event.description}
                              </Link>
                            ) : (
                              <p className="text-xs text-text-secondary mt-0.5">
                                {event.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-xs whitespace-nowrap text-text-muted pt-1.5">
                            <time>{formatTimeAgo(event.createdAt)}</time>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Metriche di Controllo</CardTitle>
                <CardDescription>
                  Attività e integrazione del grafo aziendale.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-text-secondary">
                      Connessioni Grafo
                    </span>
                    <span className="text-text-primary">82% Salute</span>
                  </div>
                  <div className="h-2 w-full bg-border-strong/20 rounded-full overflow-hidden">
                    <div
                      className="bg-primary-500 h-full rounded-full"
                      style={{ width: "82%" }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-text-secondary">
                      Seeding Documenti RAG
                    </span>
                    <span className="text-text-primary">12 Ingestiti</span>
                  </div>
                  <div className="h-2 w-full bg-border-strong/20 rounded-full overflow-hidden">
                    <div
                      className="bg-category-documents h-full rounded-full"
                      style={{ width: "48%" }}
                    />
                  </div>
                </div>
                <div className="border-t border-border pt-4 mt-2 text-center">
                  <Link
                    href="/explore"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                  >
                    Visualizza Analisi Dettagliate
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Shadow Board Recommendations */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Brain className="h-4 w-4 text-primary-500" />
                  <h3 className="text-xs font-bold tracking-wider text-text-secondary uppercase">
                    Consigli Shadow Board
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-text-muted mr-1">
                    Genera:
                  </span>
                  <button
                    className="px-1.5 py-0.5 text-[9px] font-semibold text-success hover:bg-success-soft rounded border border-success/15 cursor-pointer bg-card transition-colors"
                    onClick={() => handleGenerateProposal("low")}
                  >
                    Low
                  </button>
                  <button
                    className="px-1.5 py-0.5 text-[9px] font-semibold text-warning-strong hover:bg-warning-soft rounded border border-warning/15 cursor-pointer bg-card transition-colors"
                    onClick={() => handleGenerateProposal("medium")}
                  >
                    Med
                  </button>
                  <button
                    className="px-1.5 py-0.5 text-[9px] font-semibold text-error hover:bg-error-soft rounded border border-error/15 cursor-pointer bg-card transition-colors"
                    onClick={() => handleGenerateProposal("high")}
                  >
                    High
                  </button>
                </div>
              </div>

              {loadingProposals ? (
                <div className="space-y-3">
                  <div className="h-32 w-full animate-pulse bg-card-muted rounded-xl border border-border" />
                </div>
              ) : proposals.filter((p) => p.status === "pending").length ===
                0 ? (
                <Card className="border-dashed border-border-strong/50">
                  <CardContent className="py-6 text-center text-xs text-text-secondary">
                    <Brain className="h-6 w-6 text-text-muted mx-auto mb-2 opacity-50 animate-pulse" />
                    La Shadow Board è allineata con lo stato aziendale attuale.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {proposals
                    .filter((p) => p.status === "pending")
                    .map((proposal) => (
                      <ProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
